/**
 * Time & Attendance API Client
 *
 * Handles all time tracking, timesheets, and approval operations
 */
import { useAuthStore } from '../../stores/auth.store';
import { config } from '../config';
const TIME_API_URL = config.api.time;
/**
 * Get authorization headers with JWT token from auth store
 */
function getAuthHeaders() {
    const { tokens, user } = useAuthStore.getState();
    const token = tokens?.idToken;
    const tenantId = user?.tenantId || 'tenant-001';
    return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        'X-Tenant-Id': tenantId,
    };
}
/**
 * Handle API errors
 */
async function handleResponse(response) {
    if (!response.ok) {
        // For 404s, return a more specific error that can be detected by retry logic
        if (response.status === 404) {
            throw new Error(`HTTP 404: ${response.url}`);
        }
        const error = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(error.message || `HTTP ${response.status}`);
    }
    return response.json();
}
// ============================================================================
// Punch Clock Operations
// ============================================================================
/**
 * Create a new punch (clock in/out, break start/end)
 */
export async function createPunch(request) {
    const response = await fetch(`${TIME_API_URL}/punches`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });
    return handleResponse(response);
}
/**
 * Get the organization's mobile punch setting
 */
export async function getMobilePunchConfig() {
    const response = await fetch(`${TIME_API_URL}/config/mobile-punch`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Update the organization's self-service punch setting (admin only).
 *
 * Send `mode` alone to change who may punch while keeping the saved allowlist,
 * or send both to replace the list too.
 */
export async function updateMobilePunchConfig(payload) {
    const response = await fetch(`${TIME_API_URL}/config/mobile-punch`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(payload),
    });
    return handleResponse(response);
}
/**
 * Get current punch session for the authenticated employee
 */
export async function getCurrentSession() {
    const response = await fetch(`${TIME_API_URL}/session`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Get current punch session for a specific employee (manager access)
 */
export async function getEmployeeSession(employeeId) {
    const response = await fetch(`${TIME_API_URL}/session?employeeId=${employeeId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Get punch history for a date range
 */
export async function getPunchHistory(startDate, endDate) {
    const params = new URLSearchParams({
        startDate,
        endDate,
    });
    const response = await fetch(`${TIME_API_URL}/punches?${params}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
// ============================================================================
// Timesheet Operations
// ============================================================================
/**
 * Get timesheet for a pay period
 */
export async function getTimesheet(request) {
    const params = new URLSearchParams({
        startDate: request.startDate,
        endDate: request.endDate,
    });
    const response = await fetch(`${TIME_API_URL}/timesheets?${params}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Get timesheet summaries for current and previous pay periods
 */
export async function getTimesheets(employeeId) {
    const params = employeeId ? `?employeeId=${employeeId}` : '';
    const response = await fetch(`${TIME_API_URL}/timesheets${params}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Who is on the clock, and each employee's hours so far this pay period (admin only)
 */
export async function getTeamTimeSummary() {
    const response = await fetch(`${TIME_API_URL}/team/time-summary`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
// ============================================================================
// Pay Period Configuration (Admin Only)
// ============================================================================
/**
 * List all pay period configurations for tenant (admin only)
 */
export async function listPayPeriodConfigs() {
    const response = await fetch(`${TIME_API_URL}/config/pay-periods`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Create a new pay period configuration (admin only)
 */
export async function createPayPeriodConfig(request) {
    const response = await fetch(`${TIME_API_URL}/config/pay-periods`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });
    return handleResponse(response);
}
/**
 * Delete a pay period configuration (admin only)
 */
export async function deletePayPeriodConfig(configId) {
    const response = await fetch(`${TIME_API_URL}/config/pay-periods/${configId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
    });
    if (!response.ok) {
        const error = await response.json().catch(() => ({ message: 'Failed to delete pay period configuration' }));
        throw new Error(error.message || 'Failed to delete pay period configuration');
    }
}
/**
 * LEGACY: Get pay period configuration (deprecated - use listPayPeriodConfigs)
 */
export async function getPayPeriodConfig() {
    const response = await fetch(`${TIME_API_URL}/config/pay-period`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * LEGACY: Update pay period configuration (deprecated)
 */
export async function updatePayPeriodConfig(request) {
    const response = await fetch(`${TIME_API_URL}/config/pay-period`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });
    return handleResponse(response);
}
// ============================================================================
// Overtime Operations
// ============================================================================
/**
 * Get overtime summary
 */
export async function getOvertimeSummary(request) {
    const params = new URLSearchParams({
        startDate: request.startDate,
        endDate: request.endDate,
    });
    if (request.employeeId) {
        params.append('employeeId', request.employeeId);
    }
    const response = await fetch(`${TIME_API_URL}/overtime?${params}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Get overtime report for the team (managers only)
 */
export async function getTeamOvertimeReport(startDate, endDate) {
    const params = new URLSearchParams({
        startDate,
        endDate,
    });
    const response = await fetch(`${TIME_API_URL}/overtime/team?${params}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Create a correction request for a punch
 */
export async function createCorrection(request) {
    const response = await fetch(`${TIME_API_URL}/corrections`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });
    return handleResponse(response);
}
/**
 * Submit a timesheet for manager approval
 */
export async function submitTimesheet(request) {
    const response = await fetch(`${TIME_API_URL}/timesheets/submit`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });
    return handleResponse(response);
}
/**
 * List pending timesheet approvals (manager-only)
 */
export async function listTimesheetApprovals() {
    const response = await fetch(`${TIME_API_URL}/timesheets/approvals`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Approve or reject a timesheet (manager-only)
 */
export async function approveTimesheet(request) {
    const response = await fetch(`${TIME_API_URL}/timesheets/approve`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(request),
    });
    return handleResponse(response);
}
/**
 * List correction requests
 * @param view - 'my' for employee's own corrections, 'manager' for pending approvals
 * @param status - Filter by status (pending, approved, rejected)
 */
export async function listCorrections(view, status) {
    const params = new URLSearchParams();
    if (view)
        params.append('view', view);
    if (status)
        params.append('status', status);
    const response = await fetch(`${TIME_API_URL}/corrections?${params.toString()}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Get a single correction request by ID
 */
export async function getCorrection(correctionId) {
    const response = await fetch(`${TIME_API_URL}/corrections/${correctionId}`, {
        method: 'GET',
        headers: getAuthHeaders(),
    });
    return handleResponse(response);
}
/**
 * Approve or reject a correction request (manager-only)
 */
export async function reviewCorrection(request) {
    const { correctionId, ...body } = request;
    const response = await fetch(`${TIME_API_URL}/corrections/${correctionId}`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
        body: JSON.stringify(body),
    });
    return handleResponse(response);
}
